import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../contexts/AuthContext';

interface RoleRouteProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  fallback?: string;
}

export function RoleRoute({ children, allowedRoles, fallback = '/home/dashboard' }: RoleRouteProps) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36CCD8]"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}

// Pre-configured route guards
export function SuperAdminRoute({ children }: { children: ReactNode }) {
  return (
    <RoleRoute allowedRoles={['super_admin']} fallback="/home/dashboard">
      {children}
    </RoleRoute>
  );
}

export function AdminRoute({ children }: { children: ReactNode }) {
  const { user, roles, isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36CCD8]" /></div>;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  const condoAdmin = roles.some((membership) => ['admin', 'sub_admin', 'super_admin'].includes(membership.role || ''));
  if (!['admin', 'sub_admin', 'super_admin'].includes(user.role) && !condoAdmin) return <Navigate to="/home/dashboard" replace />;
  return <>{children}</>;
}

// F-2 (2026-09-23, standalone → condo-admin flow): /create-condo must be
// reachable by a standalone account that owns a unit with no condo link
// (not an employee assignment). Existing admin access is unchanged: any
// global/condo admin still passes, and this guard is only used for the
// /create-condo route (other admin routes keep AdminRoute).
// F-9 (2026-09-23): on a HARD load of /create-condo (direct URL or mid-wizard
// refresh) the /units/mine hydration has not settled yet, so ownedUnits is
// still [] and a standalone owner was bounced to /home/dashboard. Defer the
// redirect decision until unitsLoaded — same race as the AppShell minimalMode
// fix from earlier today. No behavior change once units are resolved.
export function CreateCondoRoute({ children }: { children: ReactNode }) {
  const { user, roles, ownedUnits, isAuthenticated, loading, unitsLoaded } = useAuth();
  if (loading || !unitsLoaded) return <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36CCD8]" /></div>;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  const condoAdmin = roles.some((membership) => ['admin', 'sub_admin', 'super_admin'].includes(membership.role || ''));
  const globalAdmin = ['admin', 'sub_admin', 'super_admin'].includes(user.role);
  const standaloneOwner = (ownedUnits || []).some((unit) => !unit.condo_id && !unit.assigned);
  if (!globalAdmin && !condoAdmin && !standaloneOwner) return <Navigate to="/home/dashboard" replace />;
  return <>{children}</>;
}

export function DirectiveRoute({ children }: { children: ReactNode }) {
  return (
    <RoleRoute allowedRoles={['directive', 'treasurer', 'propietario', 'super_admin']} fallback="/home/dashboard">
      {children}
    </RoleRoute>
  );
}

export function ResidentRoute({ children }: { children: ReactNode }) {
  return (
    <RoleRoute allowedRoles={['resident', 'propietario', 'familiar', 'directive', 'admin', 'sub_admin', 'super_admin']} fallback="/home/dashboard">
      {children}
    </RoleRoute>
  );
}

export function SecurityRoute({ children }: { children: ReactNode }) {
  // Mirrors backend authorize(canValidateQr): condo-level ['admin','sub_admin','security']
  // plus global 'security' and 'super_admin'. Global-only RoleRoute previously locked out
  // condo admins/security guards whose user.role is not 'security'.
  const { user, roles, isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36CCD8]" /></div>;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  const condoScanner = roles.some((membership) => ['security', 'admin', 'sub_admin'].includes(membership.role || ''));
  const globalAllowed = ['security', 'super_admin'].includes(user.role);
  if (!globalAllowed && !condoScanner) return <Navigate to="/home/dashboard" replace />;
  return <>{children}</>;
}

export function ProviderRoute({ children }: { children: ReactNode }) {
  return (
    <RoleRoute allowedRoles={['provider']} fallback="/home/dashboard">
      {children}
    </RoleRoute>
  );
}

// Empleado route — for employee/staff pages
export function EmpleadoRoute({ children }: { children: ReactNode }) {
  return (
    <RoleRoute allowedRoles={['empleado', 'maintenance_staff']} fallback="/home/dashboard">
      {children}
    </RoleRoute>
  );
}

// Admin or Directive (for shared pages)
export function ManagementRoute({ children }: { children: ReactNode }) {
  return (
    <RoleRoute allowedRoles={['admin', 'sub_admin', 'directive', 'treasurer', 'super_admin']} fallback="/home/dashboard">
      {children}
    </RoleRoute>
  );
}
